// MetricLineChart.js
import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import Plot from 'react-plotly.js';

const MetricLineChart = ({
  title,
  data,
  metricKey,
  timeKey = 'time',
  color = 'rgb(21, 47, 49)',
  depthData = null,
  depthColor = 'rgba(128, 128, 128, 0.5)',
  isLoading = false
}) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Prepare main metric data series
    const series = {
      type: 'scatter',
      mode: 'lines',
      name: title,
      x: data.map(item => new Date(item[timeKey])),
      y: data.map(item => item[metricKey]),
      line: { color: color }
    };

    // Only create depth series if depth data is provided
    const chartSeries = [series];

    if (depthData) {
      const depthSeries = {
        type: 'scatter',
        mode: 'lines',
        name: 'Depth',
        x: data.map(item => new Date(item[timeKey])),
        y: depthData,
        yaxis: 'y2',
        line: { color: depthColor }
      };

      chartSeries.push(depthSeries);
    }

    setChartData(chartSeries);
  }, [data, metricKey, timeKey, color, title, depthData, depthColor]);

  const layout = {
    autosize: true,
    height: 400,
    margin: { l: 50, r: 50, b: 50, t: 70, pad: 4 }, // Increased top margin for title
    title: {
      text: `${title} (${metricKey})`, // Include sensor type (metricKey) in the title
      font: {
        size: 18,
        weight: 'bold'
      }
    },
    xaxis: {
      title: 'Time',
      showgrid: false,
      zeroline: false
    },
    yaxis: {
      title: title,
      showgrid: true,
      zeroline: true
    },
    ...(depthData && {
      yaxis2: {
        title: 'Depth',
        titlefont: { color: 'gray' },
        tickfont: { color: 'gray' },
        overlaying: 'y',
        side: 'right'
      }
    }),
    legend: {
      x: 0,
      y: 1,
      traceorder: 'normal',
      orientation: 'h'
    }
  };

  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: [
      'sendDataToCloud',
      'autoScale2d',
      'hoverClosestCartesian',
      'hoverCompareCartesian',
      'lasso2d',
      'select2d'
    ]
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">{title} ({metricKey})</Typography>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">{title} ({metricKey})</Typography>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{
      borderRadius: 3,
      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)',
      mb: 3
    }}>
      <CardContent>
        <Plot
          data={chartData}
          layout={{
            ...layout,
            paper_bgcolor: 'white',
            plot_bgcolor: '#f9fafb',
          }}
          config={config}
          style={{ width: '100%' }}
        />
      </CardContent>
    </Card>
  );
};

export default MetricLineChart;